"use client";
import React from "react";
import styles from "./MatrixQuestion.module.css";
import type { MatrixConfig, SurveyQuestion } from "@/types/survey";

interface MatrixQuestionProps {
  question: SurveyQuestion;
  brandColor?: string;
  onAnswerChange?: (question: SurveyQuestion, value: string) => void;
  isPreview?: boolean;
  selectedValue?: string | number;
  onPreviewAnswerChange?: (questionId: string, value: string | number) => void;
  answerValue?: string; // non-preview stored value like "row1=3;row2=N/A"
  uiStrings?: {
    noChoicesConfigured: string;
    notApplicable: string;
  };
}

type ResolvedColumn = {
  /** Stored cell value (number for ratings, "N/A" for the skip column). */
  value: number | "N/A";
  /** Optional column label shown beneath the number. */
  text: string;
  /** Stable key for React reconciliation. */
  key: string;
};

const NA_VALUE = "N/A" as const;

/** Translate the question's matrix config (or sensible defaults) into
 *  the columns the renderer paints. Rendering and submission both
 *  derive from the same list so the cells we send back to the backend
 *  always match what the user actually saw. */
const resolveColumns = (matrix: MatrixConfig | null | undefined): ResolvedColumn[] => {
  const scale = matrix?.scale === 10 ? 10 : 5;
  const allowNA = matrix?.allowNA !== false;
  const configured = Array.isArray(matrix?.columns) ? matrix!.columns : null;

  if (configured && configured.length > 0) {
    const numeric: ResolvedColumn[] = [];
    let na: ResolvedColumn | null = null;
    for (const col of configured) {
      const text = (col?.text ?? "").trim();
      if (col?.value === NA_VALUE) {
        na = { value: NA_VALUE, text: text || NA_VALUE, key: NA_VALUE };
        continue;
      }
      const n = Number(col?.value);
      if (!Number.isFinite(n)) continue;
      numeric.push({ value: n, text, key: String(n) });
    }
    numeric.sort((a, b) => Number(a.value) - Number(b.value));
    return na ? [...numeric, na] : numeric;
  }

  // Default: bare numbers 1..scale, plus N/A when allowed.
  const numeric: ResolvedColumn[] = Array.from({ length: scale }, (_, i) => ({
    value: i + 1,
    text: "",
    key: String(i + 1),
  }));
  return allowNA
    ? [...numeric, { value: NA_VALUE, text: NA_VALUE, key: NA_VALUE }]
    : numeric;
};

// Renders a matrix (rows from choices) with configurable numeric columns
// and an optional N/A column.
export default function MatrixQuestion({
  question,
  brandColor = "var(--color-alias-info)",
  onAnswerChange,
  isPreview = false,
  selectedValue,
  onPreviewAnswerChange,
  answerValue,
  uiStrings,
}: MatrixQuestionProps) {
  const rows = Array.isArray(question.choices) ? question.choices : [];
  const columns = resolveColumns((question.matrix as MatrixConfig | null) ?? null);

  const parseAnswerMap = (raw: unknown): Record<string, string> => {
    const map: Record<string, string> = {};
    if (typeof raw !== "string" || !raw) return map;
    const parts = raw.split(";");
    for (const p of parts) {
      const [k, v] = p.split("=");
      if (k) map[k] = v ?? "";
    }
    return map;
  };

  const serializeAnswerMap = (map: Record<string, string>): string => {
    const entries = Object.entries(map)
      .filter(([, v]) => typeof v !== "undefined")
      .map(([k, v]) => `${k}=${v}`);
    return entries.join(";");
  };

  const handleChange = (rowKey: string, value: string | number) => {
    const payload = typeof value === "number" ? String(value) : String(value);
    const currentMap = isPreview
      ? parseAnswerMap(typeof selectedValue === "string" ? selectedValue : "")
      : parseAnswerMap(answerValue);
    currentMap[rowKey] = payload;
    const next = serializeAnswerMap(currentMap);
    if (isPreview && onPreviewAnswerChange) {
      onPreviewAnswerChange(question.id, next);
    } else if (onAnswerChange) {
      onAnswerChange(question, next);
    }
  };

  const handleClear = (rowKey: string) => {
    const currentMap = isPreview
      ? parseAnswerMap(typeof selectedValue === "string" ? selectedValue : "")
      : parseAnswerMap(answerValue);
    if (rowKey in currentMap) {
      delete currentMap[rowKey];
    }
    const next = serializeAnswerMap(currentMap);
    if (isPreview && onPreviewAnswerChange) {
      onPreviewAnswerChange(question.id, next);
    } else if (onAnswerChange) {
      onAnswerChange(question, next);
    }
  };

  const isCellSelected = (rowKey: string, value: string | number) => {
    const target = String(value);
    if (isPreview) {
      const map = parseAnswerMap(
        typeof selectedValue === "string" ? selectedValue : "",
      );
      return map[rowKey] === target;
    }
    const map = parseAnswerMap(answerValue);
    return map[rowKey] === target;
  };

  const questionTextStyle = { color: "var(--sl-text, var(--color-alias-neutral-darker))" };
  const mutedTextStyle = { color: "var(--sl-text-muted, var(--color-alias-neutral-dark))" };
  const rowLabelStyle = { color: "var(--sl-text, var(--color-alias-neutral-darker))" };
  const dividerStyle = { borderColor: "var(--color-alias-border-neutral)" };
  const getColumnDisplayValue = (col: ResolvedColumn) => {
    if (col.value !== NA_VALUE) return String(col.value);
    const configuredText = col.text.trim();
    return configuredText && configuredText !== NA_VALUE
      ? configuredText
      : uiStrings?.notApplicable ?? NA_VALUE;
  };

  return (
    <div className={`mb-6 ${styles.matrixContainer} text-left`}>
      <div
        className="text-sm font-medium mb-2 text-left w-full"
        style={questionTextStyle}
      >
        {question.text}
      </div>
      {/* Stacked (mobile) using container queries */}
      <div className={styles.matrixStack}>
        {rows.length === 0 ? (
          <div className="text-sm" style={mutedTextStyle}>
            {uiStrings?.noChoicesConfigured ?? "No choices configured."}
          </div>
        ) : (
          rows.map((row, idx) => {
            const rowKey = String(row?.value ?? `row_${idx + 1}`);
            return (
              <div key={rowKey} className="border-t pt-2" style={dividerStyle}>
                <div className="text-sm mb-2 text-left" style={rowLabelStyle}>
                  {row?.text}
                </div>
                <div className={`flex items-start justify-between gap-2 w-full ${styles.optionsRow}`}>
                  {columns.map((col) => {
                    const value = col.value;
                    const selected = isCellSelected(rowKey, value);
                    return (
                      <label
                        key={`${rowKey}-${col.key}`}
                        className={`inline-flex flex-col items-center justify-start gap-1 ${styles.cellLabel}`}
                        htmlFor={`mx-${question.id}-${rowKey}-${col.key}`}
                      >
                        <input
                          id={`mx-${question.id}-${rowKey}-${col.key}`}
                          type="radio"
                          name={`matrix-${question.id}-${rowKey}`}
                          value={String(value)}
                          className="sr-only"
                          checked={selected}
                          onChange={() => handleChange(rowKey, value)}
                          onClick={(e) => {
                            if (selected) {
                              e.preventDefault();
                              handleClear(rowKey);
                            }
                          }}
                        />
                        <span
                          className="w-8 h-8 rounded-md text-[12px] font-semibold flex items-center justify-center cursor-pointer border"
                          style={{
                            backgroundColor: selected ? brandColor : "transparent",
                            color: selected
                              ? "var(--color-alias-foreground-on-color)"
                              : "var(--sl-text, var(--color-alias-neutral-darkest))",
                            borderColor: selected
                              ? brandColor
                              : "var(--color-alias-neutral-light)",
                          }}
                        >
                          {getColumnDisplayValue(col)}
                        </span>
                        {col.text && col.value !== NA_VALUE ? (
                          <span className={styles.columnLabel} style={mutedTextStyle}>
                            {col.text}
                          </span>
                        ) : null}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Table (desktop) using container queries */}
      <div className={`${styles.matrixTable} overflow-auto`}>
        <table className="w-full border-collapse">
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  className="text-sm py-2"
                  style={mutedTextStyle}
                  colSpan={columns.length + 1}
                >
                  {uiStrings?.noChoicesConfigured ?? "No choices configured."}
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => {
                const rowKey = String(row?.value ?? `row_${idx + 1}`);
                return (
                  <tr key={rowKey} className="border-t" style={dividerStyle}>
                    <td className="text-sm py-2 pr-3 text-left" style={rowLabelStyle}>
                      {row?.text}
                    </td>
                    {columns.map((col) => {
                      const value = col.value;
                      const selected = isCellSelected(rowKey, value);
                      return (
                        <td
                          key={`${rowKey}-${col.key}`}
                          className={`py-1 px-1 text-center ${styles.optionCell}`}
                        >
                          <label
                            className={`inline-flex flex-col items-center justify-start gap-1 ${styles.cellLabel}`}
                            htmlFor={`mx-${question.id}-${rowKey}-${col.key}`}
                          >
                            <input
                              id={`mx-${question.id}-${rowKey}-${col.key}`}
                              type="radio"
                              name={`matrix-${question.id}-${rowKey}`}
                              value={String(value)}
                              className="sr-only"
                              checked={selected}
                              onChange={() => handleChange(rowKey, value)}
                              onClick={(e) => {
                                if (selected) {
                                  e.preventDefault();
                                  handleClear(rowKey);
                                }
                              }}
                            />
                            <span
                              className="w-8 h-8 rounded-md text-[12px] font-semibold flex items-center justify-center cursor-pointer border"
                              style={{
                                backgroundColor: selected
                                  ? brandColor
                                  : "transparent",
                                color: selected
                                  ? "var(--color-alias-foreground-on-color)"
                                  : "var(--sl-text, var(--color-alias-neutral-darkest))",
                                borderColor: selected
                                  ? brandColor
                                  : "var(--color-alias-neutral-light)",
                              }}
                            >
                              {getColumnDisplayValue(col)}
                            </span>
                            {col.text && col.value !== NA_VALUE ? (
                              <span className={styles.columnLabel} style={mutedTextStyle}>
                                {col.text}
                              </span>
                            ) : null}
                          </label>
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
